import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { NgSelectModule } from '@ng-select/ng-select';
import { finalize, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../services/api/api.service';

interface WfsCustomer {
    id: number;
    name: string;
    projects: { id: number; name: string; type: 'maintenance' | 'regular' | 'drillings' }[];
}

interface WfsToken {
    access_token: string;
    expires_at: string;
}

@Component({
    selector: 'app-settings-wfs',
    imports: [DatePipe, FormsModule, MatButtonModule, NgSelectModule],
    templateUrl: './settings-wfs.component.html',
})
export class SettingsWfsComponent {
    private apiService = inject(ApiService);

    readonly customerId = signal<number>(null);
    readonly projectId = signal<number>(null);
    readonly customers = toSignal(
        this.apiService
            .getCallData$<WfsCustomer[]>('wfs/options')
            .pipe(tap(customers => customers.length === 1 && this.customerId.set(customers[0].id))),
        { initialValue: [] }
    );
    readonly token = signal<WfsToken>(null);
    readonly generating = signal(false);
    readonly copied = signal<string>(null);

    readonly projects = computed(() => this.customers().find(c => c.id === this.customerId())?.projects ?? []);

    readonly baseUrl = computed(() => {
        const url = new URL(`${environment.apiEndpoint}wfs`);
        if (this.projectId()) {
            url.searchParams.set('customer_project_id', `${this.projectId()}`);
        }
        return url.toString();
    });

    readonly wfsUrl = computed(() => {
        if (!this.token()) {
            return null;
        }
        const url = new URL(this.baseUrl());
        url.searchParams.set('access_token', this.token().access_token);
        return url.toString();
    });

    readonly geojsonUrl = computed(() =>
        this.wfsUrl()
            ? `${this.wfsUrl()}&SERVICE=WFS&VERSION=2.0.0&REQUEST=GetFeature&TYPENAMES=planning:workarea&OUTPUTFORMAT=application/json`
            : null
    );

    selectCustomer(customerId: number) {
        this.customerId.set(customerId);
        this.projectId.set(null);
        this.token.set(null);
    }

    generateToken() {
        this.generating.set(true);
        this.apiService
            .postCall$<WfsToken>('wfs/token', { customer_id: this.customerId() })
            .pipe(finalize(() => this.generating.set(false)))
            .subscribe(response => this.token.set(response.data));
    }

    copy(value: string, what: string) {
        navigator.clipboard.writeText(value).then(() => {
            this.copied.set(what);
            setTimeout(() => this.copied.set(null), 2000);
        });
    }
}
